/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { useAtomValue } from 'jotai'
import NextLink from 'next/link'
import { usePathname as useNextPathname, useRouter as useNextRouter } from 'next/navigation'
import React from 'react'

import { selectedRegionAtom } from '@/store/region'
import { getLanguageSibling, REGION_SLUGS } from '@/utils/regions'

type NextLinkProps = React.ComponentProps<typeof NextLink>

/** Props for the regional Link component. */
type LinkProps = Omit<NextLinkProps, 'locale'> & {
  /**
   * When provided, overrides the target language for language-switching links
   * (e.g. switching from /en-gb/... to /es-es/...).
   */
  locale?: string
}

/**
 * Match a regional prefix at the start of a pathname.
 * Handles both hyphenated slugs (/en-gb/, /en-mena/) and bare slugs (/es/).
 * Only matches slugs that exist in REGION_SLUGS.
 *
 * @returns the matched slug and remaining path, or null if no match
 */
const matchRegionalPrefix = (pathname: string): { slug: string; rest: string } | null => {
  // Try hyphenated format first: /en-gb/, /en-mena/, /es-es/
  const hyphenMatch = pathname.match(/^\/([a-z]{2}-[a-z]{2,5})(\/|$)/i)
  if (hyphenMatch) {
    const slug = hyphenMatch[1].toLowerCase()
    if (REGION_SLUGS.has(slug)) {
      const rest = pathname.slice(1 + slug.length)
      return { slug, rest: rest || '/' }
    }
  }

  // Try bare format: /es/
  const bareMatch = pathname.match(/^\/([a-z]{2})(\/|$)/i)
  if (bareMatch) {
    const slug = bareMatch[1].toLowerCase()
    if (REGION_SLUGS.has(slug)) {
      const rest = pathname.slice(1 + slug.length)
      return { slug, rest: rest || '/' }
    }
  }

  return null
}

/**
 * Returns the current pathname stripped of any /{regionSlug} prefix.
 * This is what components should use for active-state checks (e.g. /dashboard/admin).
 *
 * next-intl's usePathname cannot strip the regional prefix because the browser
 * URL (/en-gb/...) doesn't match the locale segment (/en/...) that it expects.
 */
const usePathname = (): string => {
  const pathname = useNextPathname()
  const match = matchRegionalPrefix(pathname)
  return match ? match.rest : pathname
}

/**
 * Returns the current region slug prefix for URL building: /{regionSlug}.
 * When a locale prop is provided for language switching, returns the sibling region's slug.
 */
const useRegionalPrefix = (localeProp?: string): string => {
  const region = useAtomValue(selectedRegionAtom)

  if (localeProp && localeProp !== region.lang) {
    const sibling = getLanguageSibling(region)
    return `/${sibling.slug}`
  }

  return `/${region.slug}`
}

/**
 * Resolve the href for a Link, handling three cases:
 * 1. href has no regional prefix → prepend /{regionSlug}
 * 2. href has a regional prefix AND locale prop provided → switch to sibling region
 * 3. href has a regional prefix, no locale prop → keep as-is (already correct regional URL)
 *
 * Query strings and hash fragments are preserved in all cases.
 */
const resolveHref = (href: string, prefix: string, locale?: string): string => {
  if (!href.startsWith('/')) return href
  if (href.startsWith('//')) return href // protocol-relative URLs

  // Split off query string and hash so matching only applies to the pathname portion
  const qIndex = href.indexOf('?')
  const hIndex = href.indexOf('#')
  const suffixStart = qIndex !== -1 ? qIndex : hIndex !== -1 ? hIndex : -1
  const pathname = suffixStart !== -1 ? href.slice(0, suffixStart) : href
  const suffix = suffixStart !== -1 ? href.slice(suffixStart) : ''

  const match = matchRegionalPrefix(pathname)
  if (match) {
    // Already has a regional prefix
    if (locale) {
      // Language switching: replace entire prefix with the target region slug from prefix param
      const targetSlug = prefix.slice(1) // remove leading /
      return `/${targetSlug}${match.rest}${suffix}`
    }
    return href
  }

  // No regional prefix: prepend it
  return `${prefix}${pathname}${suffix}`
}

/**
 * A Link component that automatically prepends the /{regionSlug} prefix to
 * absolute hrefs. Accepts an optional `locale` prop to switch language while
 * navigating to the appropriate sibling region (used by the language switcher).
 */
const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(({ href, locale, ...props }, ref) => {
  const prefix = useRegionalPrefix(locale)

  let resolvedHref: typeof href = href
  if (typeof href === 'string') {
    resolvedHref = resolveHref(href, prefix, locale)
  } else if (typeof href === 'object' && typeof href?.pathname === 'string') {
    resolvedHref = { ...href, pathname: resolveHref(href.pathname, prefix, locale) }
  }

  return <NextLink ref={ref} href={resolvedHref} {...props} />
})
Link.displayName = 'Link'

/**
 * A useRouter hook that wraps the Next.js router, prepending /{regionSlug}
 * to absolute paths passed to push and replace.
 */
const useRouter = () => {
  const nextRouter = useNextRouter()
  const region = useAtomValue(selectedRegionAtom)

  const buildRegionalHref = (href: string): string => {
    if (!href.startsWith('/')) return href
    if (matchRegionalPrefix(href)) return href
    return `/${region.slug}${href}`
  }

  // Forwarded explicitly rather than relying on `...nextRouter`. The spread does not
  // carry these across, so `useRouter().refresh()` silently did nothing — which is
  // exactly the kind of no-op that looks like a working button until something depends
  // on it, as the error boundaries' retry did.
  return {
    back: () => nextRouter.back(),
    forward: () => nextRouter.forward(),
    refresh: () => nextRouter.refresh(),
    prefetch: (href: string) => nextRouter.prefetch(buildRegionalHref(href)),
    push: (href: string, options?: Parameters<typeof nextRouter.push>[1]) =>
      nextRouter.push(buildRegionalHref(href), options),
    replace: (href: string, options?: Parameters<typeof nextRouter.replace>[1]) =>
      nextRouter.replace(buildRegionalHref(href), options),
  }
}

export { Link, usePathname, useRouter }

// Exported for unit testing — not intended for direct use in application code.
export { resolveHref as _resolveHref, matchRegionalPrefix as _matchRegionalPrefix }
